import React from 'react';
import { useState, useEffect } from 'react';
import { collection, addDoc, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { X } from 'lucide-react';
import { BankAccount } from '../types';

type AccountType = BankAccount['type'];

interface BankAccountModalProps {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  householdId: string;
  /** When set, the modal edits this account's metadata (name, type, joint
   *  status, expected return) in place instead of creating a new one --
   *  needed so an already-existing account can be reclassified (e.g.
   *  marked joint) without losing its transaction history, which is tied
   *  to the account's Firestore id. */
  editingAccount?: BankAccount | null;
}

export default function BankAccountModal({ isOpen, onClose, userId, householdId, editingAccount }: BankAccountModalProps) {
  const [name, setName] = useState('');
  const [type, setType] = useState<AccountType>('checking');
  const [isJoint, setIsJoint] = useState(false);
  const [expectedAnnualReturn, setExpectedAnnualReturn] = useState('');
  const [initialBalance, setInitialBalance] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (editingAccount && isOpen) {
      setName(editingAccount.name);
      setType(editingAccount.type);
      setIsJoint(editingAccount.isJoint || false);
      setExpectedAnnualReturn(editingAccount.expectedAnnualReturn?.toString() || '');
      setInitialBalance(editingAccount.initialBalance.toString());
    } else if (isOpen) {
      setName('');
      setType('checking');
      setIsJoint(false);
      setExpectedAnnualReturn('');
      setInitialBalance('');
    }
  }, [editingAccount, isOpen]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !initialBalance) return;

    setLoading(true);
    try {
      const balance = Number(initialBalance);
      const finalIsJoint = (type === 'checking' || type === 'savings') ? isJoint : false;

      if (editingAccount?.id) {
        const accountData: any = {
          name,
          type,
          isJoint: finalIsJoint,
        };
        if (type.startsWith('investment_') && expectedAnnualReturn) {
          accountData.expectedAnnualReturn = Number(expectedAnnualReturn);
        } else {
          accountData.expectedAnnualReturn = null;
        }
        // initialBalance/currentBalance intentionally left untouched here --
        // editing is for metadata only, not a way to reset the ledger.
        await updateDoc(doc(db, 'bank_accounts', editingAccount.id), accountData);
      } else {
        const accountData: any = {
          userId,
          householdId,
          name,
          type,
          isJoint: finalIsJoint,
          initialBalance: balance,
          currentBalance: balance,
          createdAt: serverTimestamp()
        };
        if (type.startsWith('investment_') && expectedAnnualReturn) {
          accountData.expectedAnnualReturn = Number(expectedAnnualReturn);
        }
        await addDoc(collection(db, 'bank_accounts'), accountData);
      }

      onClose();
      setName('');
      setType('checking');
      setIsJoint(false);
      setExpectedAnnualReturn('');
      setInitialBalance('');
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl w-full max-w-md p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-600">
          <X className="w-5 h-5" />
        </button>
        <h2 className="text-xl font-bold mb-6 text-slate-900">{editingAccount ? 'Edit Bank Account' : 'Add Bank Account'}</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Account Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as AccountType)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
            >
              <optgroup label="Bank Accounts">
                <option value="checking">Checking</option>
                <option value="savings">Savings</option>
              </optgroup>
              <optgroup label="Investment Accounts">
                <option value="investment_tfsa">TFSA</option>
                <option value="investment_rrsp">RRSP</option>
                <option value="investment_resp">RESP</option>
                <option value="investment_margin">Margin / Non-Registered</option>
                <option value="investment_other">Other Investment</option>
              </optgroup>
            </select>
          </div>

          {(type === 'checking' || type === 'savings') && (
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer bg-slate-50 border border-slate-200 rounded-md px-4 py-2.5">
              <input
                type="checkbox"
                checked={isJoint}
                onChange={(e) => setIsJoint(e.target.checked)}
                className="w-4 h-4 text-blue-600 focus:ring-blue-500 rounded"
              />
              This is a joint account
            </label>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Account Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              placeholder="e.g. Chase Checking"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">Initial Balance ($)</label>
            <input
              type="number"
              step="0.01"
              value={initialBalance}
              onChange={(e) => setInitialBalance(e.target.value)}
              disabled={!!editingAccount}
              className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:opacity-60 disabled:cursor-not-allowed"
              placeholder="0.00"
              required
            />
            {editingAccount && (
              <p className="text-[11px] text-slate-400 mt-1">Balance is tracked from transactions and can't be edited here.</p>
            )}
          </div>

          {type.startsWith('investment_') && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Expected Annual Return (%)</label>
              <input
                type="number"
                step="0.1"
                value={expectedAnnualReturn}
                onChange={(e) => setExpectedAnnualReturn(e.target.value)}
                className="w-full px-4 py-2 bg-slate-50 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                placeholder="e.g. 7.5"
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full py-2.5 bg-blue-600 text-white rounded-md font-semibold text-sm hover:bg-blue-700 disabled:opacity-50 mt-4 shadow-sm transition-colors"
          >
            {loading ? 'Saving...' : (editingAccount ? 'Save Changes' : 'Add Account')}
          </button>
        </form>
      </div>
    </div>
  );
}
